import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import FileUpload, { FileAttachment } from '../components/FileUpload.tsx';
import Toast from '../components/Toast.tsx';
import { useToast } from '../hooks/useToast.ts';
import PageHeader from '../components/ui/PageHeader.tsx';
import StatusChip from '../components/ui/StatusChip.tsx';
import api from '../services/api.ts';

interface WhitelabelSettings {
  companyName: string;
  companySubtitle: string;
  logoFiles: FileAttachment[];
  primaryColor: string;
  secondaryColor: string;
}


const getBackendUrl = (path: string) => {
  if (!path) return '';
  if (path.startsWith('http://') || path.startsWith('https://') || path.startsWith('blob:')) {
    return path;
  }
  const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
  return `${baseUrl}${path}`;
};

const resolveLogoSrc = (url?: string) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('blob:')) return url;
  if (url.startsWith('/uploads/')) return getBackendUrl(url);
  return url.startsWith('/') ? getBackendUrl(url) : url;
};

const DEFAULT_PRIMARY = '#16a34a';
const DEFAULT_SECONDARY = '#15803d';
const SWATCHES = ['#16a34a', '#2563eb', '#7c3aed', '#0f766e', '#c2410c'];

const Whitelabel: React.FC = () => {
  const [settings, setSettings] = useState<WhitelabelSettings>({
    companyName: 'Meu Patrimônio',
    companySubtitle: 'Identidade visual',
    logoFiles: [],
    primaryColor: DEFAULT_PRIMARY,
    secondaryColor: DEFAULT_SECONDARY,
  });

  const { data: backendSettings, isLoading } = useQuery('whitelabel-settings', async () => {
    const response = await api.get('/settings');
    return response.data;
  });

  useEffect(() => {
    if (backendSettings) {
      setSettings(prev => ({
        ...prev,
        companyName: backendSettings.companyName || prev.companyName,
        primaryColor: backendSettings.primaryColor || prev.primaryColor,
        secondaryColor: backendSettings.secondaryColor || prev.secondaryColor,
        logoFiles: backendSettings.logoUrl
          ? [
              {
                id: 'current-logo',
                url: backendSettings.logoUrl,
                name: 'Logo atual',
                type: 'image' as const,
                file: null,
                size: -1,
              } as FileAttachment,
            ]
          : [],
      }));
    }
  }, [backendSettings]);

  const queryClient = useQueryClient();
  const { toast, showSuccess, showError, hideToast } = useToast();

  const uploadMutation = useMutation(async (file: File) => {
    const formData = new FormData();
    formData.append('files', file);
    const response = await api.post('/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  });

  const saveSettingsMutation = useMutation(
    async (settingsData: any) => {
      const response = await api.put('/settings', settingsData);
      return response.data;
    },
    {
      onSuccess: () => {
        showSuccess('Identidade salva com sucesso!');
        queryClient.invalidateQueries('whitelabel-settings');
        queryClient.invalidateQueries('settings');
        queryClient.invalidateQueries('app-settings');
      },
      onError: (error: any) => {
        console.error('Erro ao salvar configurações:', error);
        showError(error.response?.data?.error || 'Erro ao salvar configurações');
      },
    }
  );

  const mark = useMemo(() => {
    const parts = (settings.companyName || 'MP').trim().split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
  }, [settings.companyName]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      let logoUrl = '';
      if (settings.logoFiles.length > 0 && settings.logoFiles[0].file) {
        const logoUploadResult = await uploadMutation.mutateAsync(settings.logoFiles[0].file);
        logoUrl = logoUploadResult.data.files[0].url;
      } else if (settings.logoFiles.length > 0) {
        logoUrl = settings.logoFiles[0].url || '';
      }

      await saveSettingsMutation.mutateAsync({
        companyName: settings.companyName,
        logoUrl,
        primaryColor: settings.primaryColor,
        secondaryColor: settings.secondaryColor,
      });
    } catch (error) {
      console.error('Erro ao salvar configurações:', error);
    }
  };

  const resetToDefault = () => {
    if (window.confirm('Tem certeza que deseja restaurar as configurações padrão?')) {
      setSettings({
        companyName: 'Meu Patrimônio',
        companySubtitle: 'Identidade visual',
        logoFiles: [],
        primaryColor: DEFAULT_PRIMARY,
        secondaryColor: DEFAULT_SECONDARY,
      });
    }
  };

  const applySwatch = (hex: string) => {
    // Derive a darker hover from the accent for secondary.
    const n = parseInt(hex.replace('#', ''), 16);
    const r = Math.max(0, ((n >> 16) & 255) - 20);
    const g = Math.max(0, ((n >> 8) & 255) - 20);
    const b = Math.max(0, (n & 255) - 20);
    const secondary = `#${[r, g, b].map(v => v.toString(16).padStart(2, '0')).join('')}`;
    setSettings(prev => ({ ...prev, primaryColor: hex, secondaryColor: secondary }));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500" />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        breadcrumb="Config / Identidade visual"
        title="Identidade visual"
        subtitle="Logo, cores e nome · preview ao vivo"
      />

      <div className="grid-2">
        <form id="whitelabel-form" className="form-page" style={{ maxWidth: 'none' }} onSubmit={handleSubmit}>
          <div className="form-card">
            <div className="form-card-body">
              <div className="form-section-title">Identidade</div>
              <div className="form-grid">
                <label className="field full">
                  <span>Nome do produto</span>
                  <input
                    className="input"
                    name="companyName"
                    value={settings.companyName}
                    onChange={e => setSettings(prev => ({ ...prev, companyName: e.target.value }))}
                    placeholder="Meu Patrimônio"
                  />
                </label>
                <label className="field full">
                  <span>Subtítulo</span>
                  <input
                    className="input"
                    value={settings.companySubtitle}
                    onChange={e =>
                      setSettings(prev => ({ ...prev, companySubtitle: e.target.value }))
                    }
                    placeholder="Ex.: Minha empresa"
                  />
                </label>
                <div className="field full">
                  <label>Logo (PNG/SVG)</label>
                  <div className="note-callout" style={{ marginBottom: 10 }}>
                    Arraste ou selecione · recomendado 128×128 · fundo transparente
                  </div>
                  <FileUpload
                    files={settings.logoFiles}
                    onFilesChange={files => setSettings(prev => ({ ...prev, logoFiles: files }))}
                    maxFiles={1}
                    maxSizePerFile={5}
                    acceptedTypes={['image/*']}
                    showCategories={false}
                  />
                </div>
                <label className="field">
                  <span>Cor primária</span>
                  <div className="color-input-row">
                    <input
                      type="color"
                      className="color-chip"
                      style={{ padding: 0, border: '1px solid var(--slate-200)', background: settings.primaryColor }}
                      value={settings.primaryColor}
                      onChange={e =>
                        setSettings(prev => ({ ...prev, primaryColor: e.target.value }))
                      }
                    />
                    <input
                      className="input"
                      value={settings.primaryColor}
                      onChange={e =>
                        setSettings(prev => ({ ...prev, primaryColor: e.target.value }))
                      }
                    />
                  </div>
                  <div className="field-hint">Cor usada em botões, links e destaques</div>
                </label>
                <label className="field">
                  <span>Cor hover</span>
                  <div className="color-input-row">
                    <input
                      type="color"
                      className="color-chip"
                      style={{ padding: 0, border: '1px solid var(--slate-200)', background: settings.secondaryColor }}
                      value={settings.secondaryColor}
                      onChange={e =>
                        setSettings(prev => ({ ...prev, secondaryColor: e.target.value }))
                      }
                    />
                    <input
                      className="input"
                      value={settings.secondaryColor}
                      onChange={e =>
                        setSettings(prev => ({ ...prev, secondaryColor: e.target.value }))
                      }
                    />
                  </div>
                </label>
                <div className="field full">
                  <label>Paleta rápida</label>
                  <div className="wl-swatches">
                    {SWATCHES.map(hex => (
                      <button
                        key={hex}
                        type="button"
                        className={`wl-swatch${settings.primaryColor.toLowerCase() === hex ? ' active' : ''}`}
                        style={{ background: hex }}
                        onClick={() => applySwatch(hex)}
                        aria-label={`Usar cor ${hex}`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <div className="form-card-footer">
              <button type="button" className="btn btn-secondary" onClick={resetToDefault}>
                Restaurar padrão
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={saveSettingsMutation.isLoading}
              >
                {saveSettingsMutation.isLoading ? 'Salvando…' : 'Salvar identidade'}
              </button>
            </div>
          </div>
        </form>

        <div>
          <div className="card-title" style={{ marginBottom: 10 }}>
            Preview ao vivo
          </div>
          <div className="wl-preview" style={{ ['--preview-accent' as any]: settings.primaryColor }}>
            <div className="wl-preview-bar">
              {settings.logoFiles[0]?.url ? (
                <img
                  src={resolveLogoSrc(settings.logoFiles[0].url)}
                  alt="Logo"
                  style={{ width: 28, height: 28, borderRadius: 7, objectFit: 'cover' }}
                />
              ) : (
                <div
                  className="wl-preview-mark"
                  style={{
                    background: `linear-gradient(135deg, ${settings.primaryColor}, ${settings.secondaryColor})`,
                  }}
                >
                  {mark}
                </div>
              )}
              <div>
                <strong style={{ fontSize: 13, color: 'var(--slate-900)' }}>{settings.companyName}</strong>
                <div className="muted">{settings.companySubtitle}</div>
              </div>
            </div>
            <div style={{ padding: 16 }}>
              <div className="muted" style={{ marginBottom: 10 }}>
                Botão, status e link
              </div>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14 }}>
                <button
                  type="button"
                  className="btn btn-primary"
                  style={{ background: settings.primaryColor, borderColor: settings.primaryColor }}
                >
                  Ação principal
                </button>
                <StatusChip label="Ativo" tone="green" />
                <button type="button" className="link-btn" style={{ color: settings.primaryColor }}>
                  Link contextual
                </button>
              </div>
              <div className="kpi" style={{ margin: 0 }}>
                <div className="kpi-label">Exemplo KPI</div>
                <div className="kpi-value positive">R$ 12.390.000</div>
                <div className="kpi-hint">Cor {settings.primaryColor}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {toast && toast.isVisible && (
        <Toast
          type={toast.type}
          title={toast.title}
          message={toast.message}
          isVisible={toast.isVisible}
          onClose={hideToast}
        />
      )}
    </div>
  );
};

export default Whitelabel;
